import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

function formatCount(count, itemLabel) {
  const label = count === 1 ? itemLabel : `${itemLabel}s`;
  return `${count} ${label}`;
}

export default function ExportCsvDialog({
  open,
  setOpen,
  onClose,
  onExport,
  currentPageCount = 0,
  totalCount = 0,
  itemLabel = "transaction",
  title = "Export Transactions",
  description = "Choose which items to download as a CSV file.",
  pageOptionTitle = "Current page",
  pageOptionDescription,
  allOptionTitle = "All transactions",
  allOptionDescription,
  exporting = false,
}) {
  const [scope, setScope] = useState("page");

  const handleClose = () => {
    if (onClose) onClose();
    else if (setOpen) setOpen(false);
  };

  useEffect(() => {
    if (open) setScope("page");
  }, [open]);

  const handleExport = () => {
    onExport?.(scope);
    handleClose();
  };

  const resolvedPageDescription =
    pageOptionDescription ?? `${formatCount(currentPageCount, itemLabel)} visible on this page`;

  const resolvedAllDescription =
    allOptionDescription ?? `${formatCount(totalCount, itemLabel)} in total`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{title}</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
          <FormControlLabel
            value="page"
            control={<Radio size="small" />}
            label={
              <Stack>
                <Typography variant="body2" fontWeight={700}>
                  {pageOptionTitle}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {resolvedPageDescription}
                </Typography>
              </Stack>
            }
            sx={{ alignItems: "flex-start", mb: 1.5 }}
          />
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label={
              <Stack>
                <Typography variant="body2" fontWeight={700}>
                  {allOptionTitle}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {resolvedAllDescription}
                </Typography>
              </Stack>
            }
            sx={{ alignItems: "flex-start" }}
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={exporting}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleExport}
          disabled={exporting}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          Download CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
}
